import { useRef, Fragment } from 'react';
import type { Locale } from '@/strings/types';
import { useShellLocale } from './shellLocale';
import { IconTranscript } from './icons';
import useAutoScroll from './useAutoScroll';
import { useScrollFade } from './useScrollFade';
import './card.css';
import './TranscriptView.css';

/**
 * Tab 2 — Transcript.
 *
 * The point of this tab is CODE-SWITCHING: the same speaker moving between
 * Arabic and English inside one meeting, and inside single sentences. That is
 * what a single-language transcriber gets wrong, so the demo shows it rather
 * than asserting it.
 *
 * WHY THE AR SET IS NOT A TRANSLATION OF THE EN SET (2026-09-07).
 *
 * A transcript is verbatim, so the strictly realistic answer was to ship ONE
 * turn list and translate only the chrome — the recording does not change
 * language when the interface does, and the app's own Original / Translated
 * toggle exists for exactly that. It was rejected: on the Arabic homepage that
 * renders a mostly-English wall of text, and the tab's whole claim is that the
 * visitor's OWN meetings are handled. So the AR set is the same meeting with the
 * BALANCE MIRRORED — same fifteen turns, same timestamps, same four speakers,
 * same decisions reached in the same order — but Arabic-led with English terms
 * embedded, which is what a Saudi bilingual meeting actually sounds like.
 *
 * Speaker 4 keeps English as their lead language in both sets, and speaker 1
 * switches once. Without that the AR set would be uniformly Arabic and would
 * stop making the code-switching claim at all — the mix IS the demo.
 *
 * SPEAKER LABELS ARE AN EXPLICIT OWNER OVERRIDE (2026-09-05).
 *
 * copy/hero-en.tsx carries a standing ruling from 2026-08-31 that speaker
 * identification is "deliberately NOT claimed anywhere" because the feature did
 * not exist. This view was first built without labels for that reason; the
 * owner was told, and asked for the tags anyway. So they are here deliberately,
 * NOT by oversight — do not strip them citing the older rule. If the feature
 * still has not shipped by launch, this is the surface to revisit, and the
 * fallback is the timestamps-only version (drop .pntr__spk and the `spk` field;
 * nothing else depends on them).
 *
 * CONTENT POLICY: every word is synthetic, and deliberately the SAME meeting
 * as MinutesView so the two tabs cohere — a visitor who watches both sees the
 * minutes being derived from this conversation. The owner-supplied examples
 * behind the shape describe real meetings, real people, a real client and a
 * live legal dispute; none of that appears here.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF. Note that the style guide
 * governs OUR prose, not a recorded speaker's words — this is dialogue, so it
 * is deliberately more colloquial than any other Arabic on the site. What it
 * still honours: no «تم» + مصدر, Arabic commas and «؟» (§7), and no «الـ» glued
 * to a Latin term (§8).
 */

/** A run of text. A bare string is Latin-script; `ar` marks an Arabic run. */
type Run = string | { ar: string };

interface Turn {
  /** mm:ss, as the audio player shows it. */
  at: string;
  /** 1-4. Drives the chip's label and its colour slot. */
  spk: 1 | 2 | 3 | 4;
  /** True when the turn is Arabic-led, so the paragraph itself flips to RTL. */
  rtl?: boolean;
  runs: readonly Run[];
}

/* Roughly three minutes of a longer meeting. Long enough to scroll, short
   enough that the loop comes back around while a visitor is still watching.
   Mixed turns are the majority of the POINT but not the majority of the text —
   a transcript where every line switches language reads as a gimmick.

   Four voices, distributed so each one recurs: 1 leads, 2 and 3 respond and
   carry most of the Arabic, 4 raises budget and reporting. A speaker who
   appears once reads as filler rather than as a conversation. */
const EN_TURNS: readonly Turn[] = [
  {
    at: '04:12',
    spk: 1,
    runs: [
      'Let us start with the concept. We are planning a small activation for ',
      { ar: 'اليوم الوطني' },
      ', not a full-scale event.',
    ],
  },
  {
    at: '04:26',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'تمام، يعني التركيز على الديكور والتجربة البصرية أكثر من الفعاليات الطويلة؟' }],
  },
  {
    at: '04:33',
    spk: 1,
    runs: ['Exactly. Visitors pass through quickly, so anything long-form is a poor fit.'],
  },
  {
    at: '04:48',
    spk: 3,
    runs: [
      'Then we lean on the interactive side: heritage crafts, ',
      { ar: 'ركن الحناء' },
      ', and a photo booth near the entrance.',
    ],
  },
  {
    at: '05:07',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'والمأكولات؟ المكان يوفرها، فهي خارج نطاقنا.' }],
  },
  {
    at: '05:14',
    spk: 1,
    runs: ['Right. Food and beverage stay with the venue, so keep them out of the proposal.'],
  },
  {
    at: '05:29',
    spk: 3,
    rtl: true,
    runs: [{ ar: 'أقترح إضافة storytelling قصير للزوار القادمين من خارج المملكة.' }],
  },
  {
    at: '05:41',
    spk: 1,
    runs: [
      'Good. Give me three options for that: a narrator, a short film, or projection mapping.',
    ],
  },
  {
    at: '06:03',
    spk: 4,
    runs: [
      /* «ديكور» bare, not «الديكور». An English "the" in front of a definite
         Arabic noun is two articles for one noun — "the the-decor budget". The
         switched-in word takes whichever article the sentence around it has
         already supplied. Same fix at 06:52 and 07:19, and in the AR set. */
      'One question still open is the ',
      { ar: 'ديكور' },
      ' budget. We have no range to price against yet.',
    ],
  },
  {
    at: '06:15',
    spk: 2,
    rtl: true,
    /* "الحرف اليدوية" rather than bare "الحرف": undiacritized, الحرف alone reads
       as "the letter" just as readily as "the crafts". */
    runs: [{ ar: 'أتابع الموضوع هذا الأسبوع، ونسعر الحرف اليدوية على الافتراضات الحالية.' }],
  },
  {
    at: '06:31',
    spk: 1,
    runs: ['Agreed. If nothing comes back by Sunday we submit on those assumptions.'],
  },
  {
    at: '06:52',
    spk: 4,
    runs: [
      'One more thing, the reporting pack. Photos of the decor matter more here than headcount.',
    ],
  },
  {
    at: '07:04',
    spk: 3,
    rtl: true,
    runs: [{ ar: 'أتفق. نجهز قائمة باللقطات المطلوبة قبل الفعالية بأسبوع.' }],
  },
  {
    at: '07:19',
    spk: 1,
    runs: [
      'And send me ',
      { ar: 'ملف العرض' },
      ' before Thursday so I can review it with the team.',
    ],
  },
  {
    at: '07:28',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'تمام، أرسله يوم الأربعاء.' }],
  },
];

/* The mirror. Twelve Arabic-led turns to three English-led ones, against the EN
   set's nine-to-six the other way — the same conversation heard from the other
   side of the table. Speaker 4's two turns and one of speaker 1's stay English
   so the switching is still visible; drop those and the tab stops demonstrating
   anything. */
const AR_TURNS: readonly Turn[] = [
  {
    at: '04:12',
    spk: 1,
    rtl: true,
    runs: [{ ar: 'لنبدأ بالتصور. نخطط لفعالية صغيرة لليوم الوطني، وليست full-scale event.' }],
  },
  {
    at: '04:26',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'تمام، يعني التركيز على الديكور والتجربة البصرية أكثر من الفعاليات الطويلة؟' }],
  },
  {
    at: '04:33',
    spk: 1,
    rtl: true,
    runs: [{ ar: 'بالضبط. الزوار يمرون بسرعة، فأي نشاط طويل غير مناسب.' }],
  },
  {
    at: '04:48',
    spk: 3,
    rtl: true,
    /* «إذًا», not «إذن». Both spellings are attested and neither is strictly an
       error, but the reference corpus writes the alef form and §Tanween puts the
       tanween BEFORE the alef. The bare «إذا» is not available here: it would
       read as the conditional "if". */
    runs: [{ ar: 'إذًا نعتمد على الجانب التفاعلي: الحرف اليدوية وركن الحناء و photo booth قرب المدخل.' }],
  },
  {
    at: '05:07',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'والمأكولات؟ المكان يوفرها، فهي خارج نطاقنا.' }],
  },
  {
    at: '05:14',
    spk: 1,
    rtl: true,
    /* «على عاتق المكان», which is how MinutesView states the same fact. Bare
       «على المكان» reads as physically on top of the venue rather than as its
       responsibility. */
    runs: [{ ar: 'صحيح. المأكولات والمشروبات تبقى على عاتق المكان، فلا ندرجها في العرض.' }],
  },
  {
    at: '05:29',
    spk: 3,
    rtl: true,
    runs: [{ ar: 'أقترح إضافة storytelling قصير للزوار القادمين من خارج المملكة.' }],
  },
  {
    at: '05:41',
    spk: 1,
    runs: [
      'Good. Give me three options for that: ',
      { ar: 'رواية مباشرة' },
      ', a short film, or projection mapping.',
    ],
  },
  {
    at: '06:03',
    spk: 4,
    runs: [
      'One question still open is ',
      { ar: 'ميزانية الديكور' },
      '. We have no range to price against yet.',
    ],
  },
  {
    at: '06:15',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'أتابع الموضوع هذا الأسبوع، ونسعر الحرف اليدوية على الافتراضات الحالية.' }],
  },
  {
    at: '06:31',
    spk: 1,
    rtl: true,
    /* «موافق», not «متفق»: standing alone, «متفق» is an incomplete اسم مفعول —
       it wants «عليه». «موافق» is the single word that carries "Agreed." The
       turn at 07:04 keeps «أتفق», which is the verb and is fine on its own. */
    runs: [{ ar: 'موافق. إن لم يصلنا شيء قبل الأحد نقدم العرض على تلك الافتراضات.' }],
  },
  {
    at: '06:52',
    spk: 4,
    runs: [
      'One more thing, the reporting pack. Photos of ',
      { ar: 'الديكور' },
      ' matter more here than headcount.',
    ],
  },
  {
    at: '07:04',
    spk: 3,
    rtl: true,
    runs: [{ ar: 'أتفق. نجهز قائمة باللقطات المطلوبة قبل الفعالية بأسبوع.' }],
  },
  {
    at: '07:19',
    spk: 1,
    rtl: true,
    runs: [{ ar: 'وأرسل لي ملف العرض قبل الخميس لأراجعه مع الفريق.' }],
  },
  {
    at: '07:28',
    spk: 2,
    rtl: true,
    runs: [{ ar: 'تمام، أرسله يوم الأربعاء.' }],
  },
];

interface TranscriptCopy {
  cardTitle: string;
  original: string;
  translated: string;
  regionLabel: string;
  /* A function rather than a template at the call site: Arabic puts the number
     after the noun too, but a caller assembling "«متحدث» + n" would be one
     refactor away from putting it in front. */
  speaker: (n: number) => string;
  turns: readonly Turn[];
}

const COPY: Record<Locale, TranscriptCopy> = {
  en: {
    cardTitle: 'Transcript',
    original: 'Original',
    translated: 'Translated',
    regionLabel: 'Meeting transcript',
    speaker: (n) => `Speaker ${n}`,
    turns: EN_TURNS,
  },
  ar: {
    cardTitle: 'النص',
    original: 'الأصلي',
    translated: 'المترجم',
    regionLabel: 'نص الاجتماع',
    /* Digits even for 1 and 2, which §7's "spell out one, use the dual for two"
       rule does NOT reach: those govern COUNTING («ملف واحد»), and «متحدث واحد»
       would read as "one speaker" rather than as the first speaker's name. This
       is an identifier, the same slot as «الإصدار 4.2». */
    speaker: (n) => `متحدث ${n}`,
    turns: AR_TURNS,
  },
};

export default function TranscriptView() {
  const bodyRef = useRef<HTMLDivElement>(null);
  useAutoScroll(bodyRef);
  useScrollFade(bodyRef);
  const t = COPY[useShellLocale()];

  return (
    <article className="pncard">
      <header className="pncard__head">
        <span className="pncard__head-title">
          <IconTranscript size={16} />
          {t.cardTitle}
        </span>
        {/* The app's own control — .pn-meet__trans-tabs, meetings-pn.css:795.
            Static here: this is a mockup, so Translated is rendered in its real
            inactive state rather than wired to a second body. */}
        <span className="pntr__seg" aria-hidden="true">
          <span className="pntr__seg-opt is-on">{t.original}</span>
          <span className="pntr__seg-opt">{t.translated}</span>
        </span>
      </header>

      <div
        className="pncard__body pntr__body pn-scroll"
        ref={bodyRef}
        tabIndex={0}
        role="region"
        aria-label={t.regionLabel}
      >
        {/* The timestamp is a SIBLING of the text, not a child of it. Inside the
            paragraph it inherits the turn's direction, so on an Arabic turn it
            jumped to the right edge and butted against the first word — the
            times stopped being a column at all. As a flex row that keeps the
            shell's direction, the gutter stays put and only the text flips. */}
        {t.turns.map((turn) => (
          <div key={turn.at} className="pntr__turn">
            <span className="pntr__meta">
              <span className="pntr__at">{turn.at}</span>
              <span className="pntr__spk" data-spk={turn.spk}>
                {t.speaker(turn.spk)}
              </span>
            </span>
            <p
              className="pntr__text"
              dir={turn.rtl ? 'rtl' : 'ltr'}
              {...(turn.rtl ? { lang: 'ar' } : {})}
            >
              {turn.runs.map((run, i) =>
                typeof run === 'string' ? (
                  <Fragment key={i}>{run}</Fragment>
                ) : (
                  /* lang so the Arabic run picks up Plex Sans Arabic and the AR
                     leading; isolation so the neighbouring Latin comma or period
                     is not dragged to the wrong end of the run by the bidi
                     algorithm — the classic stray "., " before the phrase. */
                  <span key={i} className="pntr__ar" lang="ar">
                    {run.ar}
                  </span>
                )
              )}
            </p>
          </div>
        ))}
      </div>
    </article>
  );
}
